import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Notification } from '@models/notifications/notification.model';
import { User } from '@models/user/user.model';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { SlugifyPipe } from '@app/pipes/slugify.pipe';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private settingsPath = '/indstillinger/konto';
  constructor(
    private t: TranslatePipe,
    private slugifyPipe: SlugifyPipe,
  ) {}

  public constructUrlForItem(notification: Notification) {
    const taskRoutes: Set<string> = new Set([
      'new_offer_received',
      'offer_accepted',
      'offer_rejected',
      'offer_increase_by_TO',
      'offer_increase_by_PP',
      'offer_increase_accepted',
      'offer_increase_canceled',
      'review_received',
      'task_accepted_expired',
      'task_cancellation_requested',
      'task_cancellation_accepted',
      'task_cancellation_denied',
      'task_completed',
      'new_comment_received',
      'new_comment_on_followed_task',
      'offer_canceled',
      'task_canceled',
      'task_expired',
      'new_task',
      'not_enough_offers_in_48h',
      'request_offer',
      'offer_request_accepted',
      'request_completion',
      'book_again',
      'company_status_update',
      'offer_request_accepted',
      'offer_request_offer_made',
      'offer_request_created',
      'date-proposed',
      'day-before-reminder',
      'on-day-reminder',
    ]);

    // Predefined routes
    const predefinedRoutes = {
      'conversation-created': `/beskeder?id=${notification.convId}`,
      create_first_task: '/opret',
      'to-reactivation-1': '/opret',
      'to-reactivation-2': '/opret',
      'to-reactivation-3': '/opret',
      'to-reactivation-4': '/opret',
      'to-reactivation-5': '/opret',
      complete_profile: '/indstillinger/konto/profil-indstillinger',
      verification_need: '/indstillinger',
      offer_edit: '/opgaver/mine-opgaver/',
      offer_increase: '/opgaver/mine-opgaver/',
      new_offer_comment_received: '/opgaver/mine-opgaver/',
      task_cancellation_requested: '/opgaver/mine-opgaver/',
      penalty_applied: '/opgaver/mine-opgaver/',
      new_task: '/opgaver/',
      new_offer_received: '/opgaver/mine-opgaver/',
      request_offer: '/opgaver/mine-opgaver/',
      task_cancellation_accepted: '/opgaver/mine-opgaver/',
      task_cancellation_denied: '/opgaver/mine-opgaver/',
      offer_request_created: `/beskeder?id=${notification.convId}`,
      offer_request_offer_made: `/beskeder?id=${notification.convId}`,
      new_comment_received: '/opgaver/mine-opgaver/',
      book_again: `/beskeder?id=${notification.convId}`,
      referral_credit_referee: '/opret',
      referral_credit_referrer: '/indstillinger/konto/indbetaling',
      company_status_update: '/indstillinger/konto/badges',
      'date-accepted': `/beskeder?id=${notification.convId}`,
      support_ticket_answer: `/kontakt-chat?uid=${notification.supportTicketUid}`,
      create_support_ticket: `/kontakt-chat?uid=${notification.supportTicketUid}`,
      create_support_ticket_from_admin: `/kontakt-chat?uid=${notification.supportTicketUid}`,
    };

    if (predefinedRoutes.hasOwnProperty(notification.type)) {
      return predefinedRoutes[notification.type];
    }

    // General task related notifications
    if (taskRoutes.has(notification.type)) {
      return `/opgaver/${this.slugifyPipe.transform(
        `${notification?.task?.title} ${notification?.task?.city}`,
      )}/${notification.taskId}`;
    }
    return null;
  }

  public additionalParam(item: any) {
    const additionalParams = {
      referral_credit_referrer: 'stored_amount',
      new_offer_comment_received: `offerComment-${item?.offerCommentId}`,
      new_offer_received: `offer-${item?.offerId}`,
      new_comment_received: `comment-${item?.taskCommentId}`,
    };
    if (additionalParams.hasOwnProperty(item.type)) {
      return additionalParams[item.type];
    }
  }

  public isAccountAtRisk(
    tasksCompleted: number,
    completionRate: number,
    cancellationRate: number,
    isTaskOwner: boolean,
  ): boolean {
    if (isTaskOwner) return false;
    if (tasksCompleted <= 3 && completionRate < 70 && cancellationRate > 20) {
      return true;
    }
    return tasksCompleted > 3 && completionRate < 75 && cancellationRate > 20;
  }

  public getSideBarTabs(): {
    url: string;
    translation: string;
    svg?: string;
  }[] {
    return [
      {
        url: `${this.settingsPath}/profil-indstillinger`,
        translation: 'PAGES.SETTINGS.TABS.ACCOUNT',
        svg: `${environment.basePath}assets/svg/settings_svg/id-card.svg`,
      },
      {
        url: `${this.settingsPath}/notifikationer`,
        translation: 'PAGES.SETTINGS.TABS.NOTIFICATIONS',
        svg: `${environment.basePath}assets/svg/settings_svg/notification-bell.svg`,
      },
      {
        url: `${this.settingsPath}/betalingshistorik`,
        translation: 'PAGES.SETTINGS.TABS.PAYMENT_DASHBOARD',
        svg: `${environment.basePath}assets/svg/settings_svg/cash-wallet.svg`,
      },
      {
        url: `${this.settingsPath}/indbetaling`,
        translation: 'PLACEHOLDERS.PAYMENT_METHOD',
        svg: `${environment.basePath}assets/svg/settings_svg/credit-card.svg`,
      },
      {
        url: '/hjaelp',
        translation: 'PAGES.SETTINGS.TABS.HELP',
        svg: `${environment.basePath}assets/svg/settings_svg/help.svg`,
      },
      {
        url: '/',
        translation: 'COMPONENTS.SIDEBAR.ACCOUNT.LOGOUT',
        svg: `${environment.basePath}assets/svg/settings_svg/logout.svg`,
      },
    ];
  }

  public getSidebarTabsHandyhands(
    user: User,
  ): { url: string; translation: string }[] {
    return [
      {
        url: `${this.settingsPath}/opgave-alarm`,
        translation: 'PAGES.SETTINGS.TABS.TASK_ALERT',
      },
      {
        url: `${this.settingsPath}/handyhand-profil-indstillinger`,
        translation: 'PAGES.SETTINGS.TABS.HANDYHAND_ACCOUNT',
      },
      {
        url: `${this.settingsPath}/handyhand-panel`,
        translation: 'PAGES.SETTINGS.TABS.PP_DASHBOARD',
      },
      {
        url: `${this.settingsPath}/anmeldelser`,
        translation: 'PAGES.SETTINGS.TABS.REVIEWS',
      },
      {
        url: `${this.settingsPath}/badges`,
        translation: 'PAGES.SETTINGS.TABS.BADGES',
      },
      {
        url: `/hh/detaljer/${user.id}`,
        translation: 'PAGES.SETTINGS.TABS.PROFILE',
      },
      {
        url: '/',
        translation: 'COMPONENTS.SIDEBAR.ACCOUNT.LOGOUT',
      },
    ];
  }

  public getSettingsTab(): string[] {
    return [
      this.t.transform('PAGES.SETTINGS.TABS.ACCOUNT'),
      this.t.transform('PAGES.SETTINGS.TABS.NOTIFICATIONS'),
      this.t.transform('PAGES.SETTINGS.TABS.PAYMENT'),
      this.t.transform('PAGES.SETTINGS.TABS.PAYOUT'),
      this.t.transform('PAGES.SETTINGS.TABS.REVIEWS'),
      this.t.transform('PAGES.SETTINGS.TABS.PROFILE'),
    ];
  }

  public getGreetingMsg(): string {
    const currentHour: number = new Date().getHours();
    let greetingMsg;
    if (currentHour < 12) {
      greetingMsg = this.t.transform('PAGES.SETTINGS.TABS.GREETINGS_MORNING');
    } else if (currentHour < 18) {
      greetingMsg = this.t.transform('PAGES.SETTINGS.TABS.GREETINGS_AFTERNOON');
    } else {
      greetingMsg = this.t.transform('PAGES.SETTINGS.TABS.GREETINGS_EVENING');
    }
    return greetingMsg;
  }
}
